import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Drawer, Form, Input, Popconfirm, DatePicker, Select, Skeleton, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  SunOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import MainLayout from '../components/MainLayout';
import { leaveRequestService, LeaveRequest } from '../services/leaveRequestService';
import { employeeService, Employee } from '../services/employeeService';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PageHeader, SectionCard, EmptyState, FilterChips, PersonAvatar, Pill, Tone } from '../components/ui';
import './VacationPage.css';

type StatusKey = 'pending' | 'approved' | 'rejected';

const STATUS_TONE: Record<StatusKey, Tone> = { pending: 'warning', approved: 'success', rejected: 'danger' };

const VacationPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState<StatusKey>('pending');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  /** Last, this and next year — the old version counted up to a hardcoded 2025, so it was empty from 2026. */
  const vacationYears = useMemo(() => {
    const current = dayjs().year();
    return [current - 1, current, current + 1];
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    try {
      setLoading(true);
      setLeaveRequests(await leaveRequestService.getAll());
    } catch (error) {
      toast.error(t('vacations.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchLeaveRequests();
    employeeService
      .getAll()
      .then(setEmployees)
      .catch(() => toast.error(t('vacations.employeesFetchError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** This controller answers errors as { error: … }, unlike the rest of the API. */
  const errorMessage = (error: any, fallback: string) =>
    error.response?.data?.error ||
    error.response?.data?.message ||
    (error.response?.data?.errors
      ? (Object.values(error.response.data.errors as Record<string, string[]>)[0] ?? [])[0]
      : undefined) ||
    fallback;

  const runAction = async (id: number, action: () => Promise<unknown>, successKey: string, errorKey: string) => {
    try {
      setBusyId(id);
      await action();
      toast.success(t(successKey));
      await fetchLeaveRequests();
    } catch (error: any) {
      toast.error(errorMessage(error, t(errorKey)));
    } finally {
      setBusyId(null);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setSaving(true);
      await leaveRequestService.create({
        ...values,
        start_date: values.start_date.format('YYYY-MM-DD'),
        end_date: values.end_date.format('YYYY-MM-DD'),
        status: 'pending',
      });
      toast.success(t('vacations.createSuccess'));
      setDrawerVisible(false);
      form.resetFields();
      // The backend approves anything an admin adds, so show that list
      setStatus('approved');
      fetchLeaveRequests();
    } catch (error: any) {
      toast.error(errorMessage(error, t('vacations.createError')));
    } finally {
      setSaving(false);
    }
  };

  // ---------- List ----------
  const employeeName = useCallback(
    (employeeId: number) => {
      const employee = employees.find((item) => item.id === employeeId);
      return employee ? `${employee.name} ${employee.surname}` : `#${employeeId}`;
    },
    [employees],
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const searched = useMemo(
    () =>
      leaveRequests.filter(
        (request) =>
          !normalizedSearch || employeeName(request.employee_id).toLowerCase().includes(normalizedSearch),
      ),
    [leaveRequests, normalizedSearch, employeeName],
  );

  const counts = useMemo(() => {
    const result: Record<StatusKey, number> = { pending: 0, approved: 0, rejected: 0 };
    searched.forEach((request) => {
      const key = (request.status ?? 'pending') as StatusKey;
      if (key in result) result[key] += 1;
    });
    return result;
  }, [searched]);

  const visible = useMemo(
    () =>
      searched
        .filter((request) => (request.status ?? 'pending') === status)
        .sort((a, b) => b.start_date.localeCompare(a.start_date)),
    [searched, status],
  );

  const leaveTypeLabel = (type: string) =>
    type === 'Jahresurlaub' ? t('vacations.annualLeave') : type === 'Krankheitsurlaub' ? t('vacations.sickLeave') : type;

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <MainLayout bare>
      <PageHeader
        title={t('vacations.title')}
        subtitle={
          loading
            ? undefined
            : t('vacations.summary', {
                pending: counts.pending,
                approved: counts.approved,
                rejected: counts.rejected,
              })
        }
        actions={
          <>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: 'var(--e22-text-muted)' }} />}
              placeholder={t('vacations.searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{ width: 260 }}
            />
            <Button icon={<ReloadOutlined spin={loading} />} onClick={fetchLeaveRequests} disabled={loading}>
              {t('dashboard.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setDrawerVisible(true);
              }}
            >
              {t('vacations.addVacation')}
            </Button>
          </>
        }
      />

      <SectionCard
        title={t(`vacations.${status}Requests`)}
        icon={<SunOutlined />}
        subtitle={loading ? undefined : t('tasks.assignmentCount', { count: visible.length })}
        action={
          <FilterChips<StatusKey>
            ariaLabel={t('vacations.status')}
            value={status}
            onChange={setStatus}
            options={[
              { key: 'pending', label: t('vacations.pending'), count: counts.pending },
              { key: 'approved', label: t('vacations.approved'), count: counts.approved },
              { key: 'rejected', label: t('vacations.rejected'), count: counts.rejected },
            ]}
          />
        }
      >
        {loading ? (
          <Skeleton active avatar paragraph={{ rows: 5 }} />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={normalizedSearch ? <SearchOutlined /> : <SunOutlined />}
            text={normalizedSearch ? t('vacations.noMatching') : t('vacations.noRequests')}
            action={
              normalizedSearch ? (
                <button type="button" className="e22-link-button" onClick={() => setSearchTerm('')}>
                  {t('employees.clearSearch')}
                </button>
              ) : undefined
            }
          />
        ) : (
          visible.map((request) => {
            const name = employeeName(request.employee_id);
            const [first, ...rest] = name.split(' ');
            const sick = request.type === 'Krankheitsurlaub';
            return (
              <article className="vac-card" key={request.id}>
                <PersonAvatar name={first} surname={rest.join(' ')} size={42} />

                <div className="vac-body">
                  <div className="vac-head">
                    <span className="e22-row-title">{name}</span>
                    <Pill tone={sick ? 'danger' : 'warning'}>{leaveTypeLabel(request.type)}</Pill>
                    <Pill tone={STATUS_TONE[(request.status ?? 'pending') as StatusKey]}>
                      {t(`vacations.statuses.${request.status ?? 'pending'}`)}
                    </Pill>
                  </div>

                  <div className="vac-dates">
                    {formatDate(request.start_date)} – {formatDate(request.end_date)}
                    {request.vacation_days ? (
                      <span className="vac-days">
                        · {t('dashboard.workingDays', { count: request.vacation_days })}
                      </span>
                    ) : null}
                  </div>

                  {request.reason ? <p className="vac-reason">{request.reason}</p> : null}

                  <div className="vac-meta">
                    {t('vacations.vacationYear')}: <b>{request.vacation_year}</b>
                    {request.approved_days_this_year !== undefined ? (
                      <>
                        {' · '}
                        {t('vacations.approvedDaysInYear', { count: Number(request.approved_days_this_year) })}
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="vac-actions">
                  {(request.status ?? 'pending') === 'pending' && (
                    <>
                      <Popconfirm
                        title={t('vacations.approveConfirm')}
                        description={name}
                        onConfirm={() =>
                          runAction(
                            request.id,
                            () => leaveRequestService.approve(request.id),
                            'vacations.approveSuccess',
                            'vacations.approveError',
                          )
                        }
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                      >
                        <Button type="primary" icon={<CheckOutlined />} loading={busyId === request.id}>
                          {t('vacations.approve')}
                        </Button>
                      </Popconfirm>
                      <Popconfirm
                        title={t('vacations.rejectConfirm')}
                        description={name}
                        onConfirm={() =>
                          runAction(
                            request.id,
                            () => leaveRequestService.reject(request.id),
                            'vacations.rejectSuccess',
                            'vacations.rejectError',
                          )
                        }
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        okButtonProps={{ danger: true }}
                      >
                        <Button icon={<CloseOutlined />} loading={busyId === request.id}>
                          {t('vacations.reject')}
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                  <Popconfirm
                    title={t('vacations.deleteConfirm')}
                    description={name}
                    onConfirm={() =>
                      runAction(
                        request.id,
                        () => leaveRequestService.delete(request.id),
                        'vacations.deleteSuccess',
                        'vacations.deleteError',
                      )
                    }
                    okText={t('common.yes')}
                    cancelText={t('common.no')}
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title={t('common.delete')}>
                      <Button type="text" danger icon={<DeleteOutlined />} loading={busyId === request.id} />
                    </Tooltip>
                  </Popconfirm>
                </div>
              </article>
            );
          })
        )}
      </SectionCard>

      <Drawer
        title={t('vacations.newLeaveRequest')}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={500}
        footer={
          <div className="vac-drawer-footer">
            <Button onClick={() => setDrawerVisible(false)}>{t('common.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {t('common.create')}
            </Button>
          </div>
        }
      >
        <div className="vac-hint">
          <InfoCircleOutlined />
          <span>{t('vacations.addedApprovedHint')}</span>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ vacation_year: String(dayjs().year()) }}>
          <Form.Item
            name="employee_id"
            label={t('vacations.employee')}
            rules={[{ required: true, message: t('vacations.employeeRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('vacations.selectEmployee')}
              options={employees.map((employee) => ({
                value: employee.id!,
                label: `${employee.name} ${employee.surname}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label={t('vacations.type')}
            rules={[{ required: true, message: t('vacations.typeRequired') }]}
          >
            <Select
              placeholder={t('vacations.selectLeaveType')}
              options={[
                { value: 'Jahresurlaub', label: t('vacations.annualLeave') },
                { value: 'Krankheitsurlaub', label: t('vacations.sickLeave') },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="vacation_year"
            label={t('vacations.vacationYear')}
            rules={[{ required: true, message: t('vacations.vacationYearRequired') }]}
          >
            <Select
              placeholder={t('vacations.selectVacationYear')}
              options={vacationYears.map((year) => ({ value: String(year), label: String(year) }))}
            />
          </Form.Item>

          <Form.Item
            name="start_date"
            label={t('vacations.startDate')}
            rules={[{ required: true, message: t('vacations.startDateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="end_date"
            label={t('vacations.endDate')}
            rules={[{ required: true, message: t('vacations.endDateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            name="reason"
            label={t('vacations.reason')}
            rules={[{ required: true, message: t('vacations.reasonRequired') }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Drawer>
    </MainLayout>
  );
};

export default VacationPage;
